/**
 * Backup & restore — /super-admin/backup (Super Admin, Administration group)
 *
 * Two halves, both on one screen:
 *   Back up  — tick what to include, download a single .zip (database, settings, uploaded files).
 *   Restore  — pick a .zip, "Check archive" first (previews every change, writes nothing),
 *              then run it in merge or replace mode.
 *
 * Everything is a panel on the page, no dialogs; the restore preview opens in the side pane.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { PaneFooter } from '../../components/shell/SidePane';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  Archive, DownloadSimple, UploadSimple, Database, Gear, FolderOpen, Users, ShieldWarning,
  ClockCounterClockwise, CheckCircle, XCircle, Warning, ArrowCounterClockwise, Eye, Trash,
  FileZip, Key, HardDrives,
} from '@phosphor-icons/react';
import { BRAND_SLUG } from '../../brand';

const MB = 1024 * 1024;

const prettyBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < MB) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * MB) return `${(b / MB).toFixed(1)} MB`;
  return `${(b / (1024 * MB)).toFixed(2)} GB`;
};

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Human labels for the collections, so the page does not read like a database dump. */
const GROUPS = [
  { label: 'People & organisations', collections: ['users', 'tenants'] },
  { label: 'Courses & content', collections: ['courses', 'course_nodes'] },
  { label: 'Question bank & exams', collections: ['question_bank', 'question_categories', 'exams', 'exam_attempts'] },
  { label: 'Learners’ progress', collections: ['enrollments', 'batches', 'node_progress', 'certificates'] },
  { label: 'Notes & community', collections: ['lesson_notes', 'lesson_posts', 'lesson_replies'] },
  { label: 'Leads & admissions', collections: ['crm_leads', 'crm_activities', 'crm_config'] },
  { label: 'Files & activity', collections: ['files', 'activity_logs', 'password_resets'] },
];

const SETTING_LABEL = { storage: 'Storage (Cloudflare R2)', smtp: 'Email (SMTP)', railway: 'Railway domains', domains: 'Platform domains' };

const Row = ({ label, hint, children, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-[13px] font-medium" data-find={label}>{label}</p>
      {hint && <p className="field-hint mt-0.5">{hint}</p>}
    </div>
    <div className="flex-shrink-0 pt-0.5">{children}</div>
  </div>
);

export default function BackupRestore() {
  const { api } = useAuth();
  const fileInput = useRef(null);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // what goes into the backup
  const [opts, setOpts] = useState({
    include_database: true,
    include_settings: true,
    include_files: false,
    include_credentials: false,
    file_budget_mb: 250,
  });

  // the restore side
  const [archive, setArchive] = useState(null);      // the chosen File
  const [mode, setMode] = useState('merge');
  const [restoreOpts, setRestoreOpts] = useState({ include_settings: true, include_files: true });
  const [confirmWord, setConfirmWord] = useState('');
  const [checking, setChecking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [preview, setPreview] = useState(null);      // { manifest, plan, filename }
  const [outcome, setOutcome] = useState(null);      // result of the last restore
  const [paneOpen, setPaneOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSummary((await api.get('/admin/backup/summary')).data); }
    catch { toast.error('Could not read the platform summary'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const counts = summary?.collections || {};
  const files = useMemo(() => summary?.files || {}, [summary]);
  const settings = summary?.settings || {};
  const history = summary?.history || [];
  const configuredSettings = Object.entries(settings).filter(([, s]) => s.configured).map(([k]) => k);

  const update = (patch) => setOpts((p) => ({ ...p, ...patch }));

  const handleExport = async () => {
    if (!opts.include_database && !opts.include_settings && !opts.include_files) {
      toast.error('Pick at least one thing to back up.');
      return;
    }
    setExporting(true);
    try {
      const res = await api.post('/admin/backup/export', opts, { responseType: 'blob' });
      const name = res.headers?.['x-backup-filename']
        || /filename="?([^"]+)"?/.exec(res.headers?.['content-disposition'] || '')?.[1]
        || `${BRAND_SLUG}-backup.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success(`Backup downloaded — ${name} (${prettyBytes(res.data.size)})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'The backup could not be built');
    } finally {
      setExporting(false);
    }
  };

  const pickArchive = (e) => {
    const f = e.target.files?.[0];
    setPreview(null);
    setOutcome(null);
    setConfirmWord('');
    setArchive(f || null);
  };

  const form = () => {
    const fd = new FormData();
    fd.append('file', archive);
    fd.append('mode', mode);
    fd.append('include_settings', String(restoreOpts.include_settings));
    fd.append('include_files', String(restoreOpts.include_files));
    return fd;
  };

  const handleCheck = async () => {
    if (!archive) { toast.error('Choose a backup file first.'); return; }
    setChecking(true);
    setOutcome(null);
    try {
      const res = await api.post('/admin/backup/inspect', form());
      setPreview(res.data);
      setPaneOpen(true);
      toast.success('Archive read — nothing has been written yet');
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.detail || 'That archive could not be read');
    } finally {
      setChecking(false);
    }
  };

  const handleRestore = async () => {
    if (!archive) { toast.error('Choose a backup file first.'); return; }
    if (mode === 'replace' && confirmWord.trim().toUpperCase() !== 'REPLACE') {
      toast.error('Type REPLACE to confirm a replace restore.');
      return;
    }
    setRestoring(true);
    try {
      const fd = form();
      if (mode === 'replace') fd.append('confirm', confirmWord.trim().toUpperCase());
      const res = await api.post('/admin/backup/restore', fd);
      setOutcome(res.data);
      setPaneOpen(true);
      toast.success(`Restored ${res.data.documents_written} documents`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'The restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const clearHistory = async () => {
    try { await api.delete('/admin/backup/history'); toast.success('Backup log cleared'); load(); }
    catch { toast.error('Could not clear the log'); }
  };

  const budgetHint = useMemo(() => {
    if (!files.count) return 'No uploaded files on this platform yet.';
    if (!files.downloadable) return 'Object storage is not configured, so the files themselves cannot be pulled into a backup — only their details.';
    return `${files.count} file${files.count === 1 ? '' : 's'}, ${prettyBytes(files.total_bytes)} in total${files.stream_videos ? `. ${files.stream_videos} video(s) on Cloudflare Stream cannot be downloaded and are always skipped.` : '.'}`;
  }, [files]);

  const pane = (preview || outcome) ? {
    open: paneOpen,
    onOpenChange: setPaneOpen,
    title: outcome ? 'Restore result' : 'What this restore would change',
    subtitle: outcome ? `${outcome.documents_written} documents written in ${outcome.mode} mode` : preview?.filename,
    icon: outcome ? CheckCircle : Eye,
    storageKey: 'backup-restore-pane',
    children: <RestoreDetail preview={preview} outcome={outcome} />,
    footer: <PaneFooter onCancel={() => setPaneOpen(false)} cancelLabel="Close" />,
    testid: 'restore-pane',
  } : null;

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="backup-restore-page"
        title="Backup & restore"
        subtitle="One archive with the database, the platform settings and the uploaded files"
        badge={<StatusBadge value={history[0] ? `last backup ${when(history.find((h) => h.action === 'export')?.created_at).split(',')[0]}` : 'never backed up'} tone={history.some((h) => h.action === 'export') ? 'success' : 'warning'} />}
        onBack={null}
        onRefresh={load}
        refreshing={loading}
        loading={loading && !summary}
        primary={[
          { key: 'export', label: exporting ? 'Building…' : 'Download backup', icon: DownloadSimple, onClick: handleExport, disabled: exporting, testid: 'download-backup-btn' },
        ]}
        secondary={[
          { key: 'reload', label: 'Refresh', icon: ArrowCounterClockwise, variant: 'outline', onClick: load, disabled: loading },
        ]}
        side={pane}
      >
        <StatTiles items={[
          { label: 'Documents', value: (summary?.total_documents ?? 0).toLocaleString(), icon: Database, testid: 'tile-documents' },
          { label: 'Uploaded files', value: files.count ? `${files.count} · ${prettyBytes(files.total_bytes)}` : '0', icon: FolderOpen, iconClass: 'bg-sky-100 text-sky-700', testid: 'tile-files' },
          { label: 'Settings saved', value: `${configuredSettings.length} of 4`, icon: Gear, iconClass: 'bg-violet-100 text-violet-700', testid: 'tile-settings' },
          { label: 'Backups taken', value: history.filter((h) => h.action === 'export').length, icon: Archive, iconClass: history.some((h) => h.action === 'export') ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700', testid: 'tile-backups' },
        ]} />

        <div className="grid gap-3 lg:grid-cols-2 items-start">
          {/* ------------------------------------------------------------------ back up */}
          <div className="space-y-3">
            <FormSection title="What to back up" icon={Archive} bodyClassName="p-3 divide-y divide-border">
              <Row label="Database" hint={`All ${Object.keys(counts).length} collections — ${(summary?.total_documents ?? 0).toLocaleString()} documents, including users with their passwords so everyone can sign in after a restore.`} testid="opt-database">
                <Switch checked={opts.include_database} onCheckedChange={(v) => update({ include_database: v })} data-testid="include-database-switch" />
              </Row>
              <Row label="Platform settings" hint={configuredSettings.length ? `Saved: ${configuredSettings.map((k) => SETTING_LABEL[k] || k).join(', ')}.` : 'Nothing configured yet.'} testid="opt-settings">
                <Switch checked={opts.include_settings} onCheckedChange={(v) => update({ include_settings: v })} data-testid="include-settings-switch" />
              </Row>
              <Row label="Uploaded files" hint={budgetHint} testid="opt-files">
                <Switch checked={opts.include_files} onCheckedChange={(v) => update({ include_files: v })} disabled={!files.count || !files.downloadable} data-testid="include-files-switch" />
              </Row>
              {opts.include_files && (
                <div className="pt-2">
                  <div className="field">
                    <Label htmlFor="file_budget_mb">Stop after (MB of files)</Label>
                    <Input id="file_budget_mb" type="number" min="1" max={summary?.max_file_budget_mb || 2048}
                      value={opts.file_budget_mb}
                      onChange={(e) => update({ file_budget_mb: parseInt(e.target.value, 10) || 0 })}
                      data-testid="file-budget-input" />
                    <p className="field-hint">Keeps one archive to a sensible size. Anything over the budget is listed as skipped inside the backup.</p>
                  </div>
                </div>
              )}
              <Row label="Include service credentials" hint="The R2 keys, the SMTP password and the Railway API token. Left out, they are written as a placeholder and a restore keeps whatever the target already has." testid="opt-credentials">
                <Switch checked={opts.include_credentials} onCheckedChange={(v) => update({ include_credentials: v })} disabled={!opts.include_settings} data-testid="include-credentials-switch" />
              </Row>
            </FormSection>

            <div className="flex items-start gap-2 text-[12px] p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800" data-testid="sensitive-warning">
              <ShieldWarning className="w-4 h-4 flex-shrink-0 mt-0.5" weight="fill" />
              <span>
                A backup carries personal data and password hashes{opts.include_credentials ? ', and with credentials on, your storage keys and SMTP password in plain text' : ''}.
                Keep the file as carefully as you would keep the database itself.
              </span>
            </div>

            <FormSection title="In the archive" icon={FileZip} bodyClassName="p-0">
              <table className="w-full text-[12px]">
                <tbody className="divide-y divide-border">
                  {GROUPS.map((g) => (
                    <tr key={g.label}>
                      <td className="px-3 py-1.5" data-find={g.label}>{g.label}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {g.collections.reduce((n, c) => n + (counts[c] || 0), 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 text-[11px] text-muted-foreground truncate max-w-[200px]">{g.collections.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FormSection>
          </div>

          {/* ------------------------------------------------------------------ restore */}
          <div className="space-y-3">
            <FormSection title="Restore from a backup" icon={UploadSimple} bodyClassName="p-3 space-y-3">
              <div className="field">
                <Label htmlFor="archive">Backup file (.zip)</Label>
                <input ref={fileInput} id="archive" type="file" accept=".zip,application/zip"
                  onChange={pickArchive} className="block w-full text-[13px] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[12px] file:font-medium file:bg-shell-primary/10 file:text-shell-primary hover:file:bg-shell-primary/15 cursor-pointer"
                  data-testid="archive-input" />
                {archive && <p className="field-hint">{archive.name} · {prettyBytes(archive.size)}</p>}
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>How to write it</Label>
                {[
                  { key: 'merge', title: 'Merge', hint: 'Keep what is here and write each record from the backup over the one with the same id. Nothing is deleted. Safe to re-run.' },
                  { key: 'replace', title: 'Replace', hint: 'Empty each collection in the backup first, so the platform ends up exactly as it was. Anything created since the backup is lost.' },
                ].map((m) => (
                  <label key={m.key} className={`flex gap-2.5 p-2.5 rounded-md border cursor-pointer ${mode === m.key ? 'border-shell-primary bg-shell-primary/5' : 'border-border hover:bg-muted/40'}`} data-testid={`mode-${m.key}`}>
                    <input type="radio" name="restore-mode" value={m.key} checked={mode === m.key}
                      onChange={() => { setMode(m.key); setConfirmWord(''); }} className="mt-0.5" />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium" data-find={m.title}>{m.title}{m.key === 'replace' && <span className="ml-1.5 text-[10px] uppercase font-semibold text-red-700">destructive</span>}</span>
                      <span className="block field-hint mt-0.5">{m.hint}</span>
                    </span>
                  </label>
                ))}
              </div>

              <Separator />

              <div className="divide-y divide-border">
                <Row label="Also restore the settings" hint="Storage, SMTP, Railway and platform domains. Credentials the archive left out are kept as they are here.">
                  <Switch checked={restoreOpts.include_settings} onCheckedChange={(v) => setRestoreOpts((p) => ({ ...p, include_settings: v }))} data-testid="restore-settings-switch" />
                </Row>
                <Row label="Put the uploaded files back" hint="Writes the objects inside the archive back into storage. Needs Cloudflare R2 or Emergent storage to be configured.">
                  <Switch checked={restoreOpts.include_files} onCheckedChange={(v) => setRestoreOpts((p) => ({ ...p, include_files: v }))} data-testid="restore-files-switch" />
                </Row>
              </div>

              {mode === 'replace' && (
                <div className="field" data-testid="confirm-block">
                  <Label htmlFor="confirm">Type REPLACE to confirm</Label>
                  <Input id="confirm" value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)}
                    placeholder="REPLACE" autoComplete="off" data-testid="confirm-input" />
                  <p className="field-hint">This empties every collection the backup covers before writing. Take a fresh backup first.</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={handleCheck} disabled={!archive || checking} data-testid="check-archive-btn">
                  <Eye className="w-4 h-4" /> {checking ? 'Reading…' : 'Check archive'}
                </Button>
                <Button size="sm" onClick={handleRestore} disabled={!archive || restoring || (mode === 'replace' && confirmWord.trim().toUpperCase() !== 'REPLACE')}
                  className={mode === 'replace' ? 'bg-red-600 hover:bg-red-700' : undefined} data-testid="run-restore-btn">
                  <UploadSimple className="w-4 h-4" /> {restoring ? 'Restoring…' : mode === 'replace' ? 'Replace everything' : 'Merge into platform'}
                </Button>
                {(preview || outcome) && !paneOpen && (
                  <Button variant="ghost" size="sm" onClick={() => setPaneOpen(true)}>Show details</Button>
                )}
              </div>
              <p className="field-hint">“Check archive” reads the file and lists every change without writing anything — always worth doing first.</p>
            </FormSection>

            <FormSection title="Backup log" icon={ClockCounterClockwise}
              actions={history.length ? <Button variant="ghost" size="sm" onClick={clearHistory} data-testid="clear-log-btn"><Trash className="w-3.5 h-3.5" /> Clear</Button> : null}
              bodyClassName="p-0">
              {history.length === 0 ? (
                <p className="p-3 text-[12px] text-muted-foreground" data-testid="history-empty">
                  No backup has been taken from this platform yet. Download one now and keep it somewhere outside the hosting account.
                </p>
              ) : (
                <table className="w-full text-[12px]" data-testid="history-table">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-3 py-1.5">When</th>
                      <th className="text-left font-medium px-3 py-1.5">What</th>
                      <th className="text-right font-medium px-3 py-1.5">Docs</th>
                      <th className="text-left font-medium px-3 py-1.5">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {history.map((h) => (
                      <tr key={h.id} data-testid={`history-${h.action}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">{when(h.created_at)}</td>
                        <td className="px-3 py-1.5">
                          <span className="inline-flex items-center gap-1.5">
                            {h.action === 'export'
                              ? <><DownloadSimple className="w-3.5 h-3.5 text-shell-primary" /> Backup{h.credentials_redacted === false && <Key className="w-3 h-3 text-amber-600" title="credentials included" />}</>
                              : <><UploadSimple className="w-3.5 h-3.5 text-amber-600" /> Restore ({h.mode})</>}
                          </span>
                          {h.size_bytes ? <span className="text-muted-foreground"> · {prettyBytes(h.size_bytes)}</span> : null}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{(h.total_documents || 0).toLocaleString()}</td>
                        <td className="px-3 py-1.5 truncate max-w-[160px] text-muted-foreground">{h.by?.email || h.by?.name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </FormSection>

            <FormSection title="Where the uploads live" icon={HardDrives} bodyClassName="p-3">
              <p className="text-[12px] text-muted-foreground">
                Videos, PDFs and audio sit in object storage (Cloudflare R2 or Emergent), not in the database — the
                database only holds their details. Keep the same bucket and the uploads keep working after any restore,
                whether or not they were in the archive.
                {files.r2_configured ? ' Cloudflare R2 is configured on this platform.' : ' Cloudflare R2 is not configured here yet.'}
              </p>
            </FormSection>
          </div>
        </div>
      </RecordPage>
    </DashboardLayout>
  );
}

/** The side-pane body: the plan before a restore, or what the restore actually did. */
function RestoreDetail({ preview, outcome }) {
  const manifest = outcome?.manifest || preview?.manifest;
  const plan = outcome?.plan || preview?.plan;
  const steps = (plan?.steps || []).filter((s) => s.action !== 'skipped');
  const skipped = (plan?.steps || []).filter((s) => s.action === 'skipped');

  return (
    <div className="p-3 space-y-3 text-[12px]" data-testid="restore-detail">
      {manifest && (
        <div className="rounded-md border border-border bg-muted/30 p-2.5 space-y-1">
          <p className="font-medium text-[13px]">The archive</p>
          <p className="text-muted-foreground">Taken {when(manifest.created_at)} by {manifest.created_by?.email || '—'} on v{manifest.app_version}</p>
          <p className="text-muted-foreground">
            {(manifest.total_documents || 0).toLocaleString()} documents · {manifest.files?.included || 0} uploaded file(s)
            {manifest.credentials_redacted ? ' · credentials redacted' : ' · credentials included'}
          </p>
        </div>
      )}

      {outcome?.warnings?.length > 0 && outcome.warnings.map((w) => (
        <div key={w} className="flex items-start gap-2 p-2.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800" data-testid="restore-warning">
          <Warning className="w-4 h-4 flex-shrink-0 mt-0.5" weight="fill" /><span>{w}</span>
        </div>
      ))}

      {!outcome && plan?.documents_removed > 0 && (
        <div className="flex items-start gap-2 p-2.5 rounded-md border border-red-200 bg-red-50 text-red-800" data-testid="removal-warning">
          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" weight="fill" />
          <span><strong>{plan.documents_removed.toLocaleString()}</strong> document(s) here are not in the backup and a replace restore would delete them.</span>
        </div>
      )}

      {outcome && (
        <div className="flex items-start gap-2 p-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800">
          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" weight="fill" />
          <span>{outcome.documents_written.toLocaleString()} documents written in {outcome.mode} mode.
            {' '}{outcome.files?.restored ? `${outcome.files.restored} uploaded file(s) put back.` : ''}</span>
        </div>
      )}

      {outcome?.files?.notes?.length > 0 && (
        <div className="rounded-md border border-border p-2.5 space-y-1" data-testid="file-notes">
          <p className="font-medium">Uploaded files</p>
          {outcome.files.notes.map((n) => <p key={n} className="text-muted-foreground">{n}</p>)}
        </div>
      )}

      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-2.5 py-1.5">Collection</th>
              <th className="text-right font-medium px-2.5 py-1.5">In backup</th>
              <th className="text-right font-medium px-2.5 py-1.5">Here now</th>
              <th className="text-right font-medium px-2.5 py-1.5">{outcome ? 'Written' : 'New'}</th>
              <th className="text-right font-medium px-2.5 py-1.5">Deleted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {steps.map((s) => {
              const written = outcome?.collections?.find((c) => c.collection === s.collection)?.written;
              return (
                <tr key={s.collection} data-testid={`plan-${s.collection}`}>
                  <td className="px-2.5 py-1.5">{s.collection}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{s.in_archive.toLocaleString()}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums text-muted-foreground">{s.current.toLocaleString()}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{(outcome ? (written ?? 0) : s.added).toLocaleString()}</td>
                  <td className={`px-2.5 py-1.5 text-right tabular-nums ${s.removed ? 'text-red-700 font-medium' : 'text-muted-foreground'}`}>{s.removed.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {skipped.length > 0 && (
        <p className="text-muted-foreground">
          <Users className="w-3.5 h-3.5 inline mr-1" />
          Left alone: {skipped.map((s) => `${s.collection} (${s.note})`).join(', ')}.
        </p>
      )}

      {!outcome && <p className="field-hint">Nothing has been written. Close this and press the restore button to go ahead.</p>}
    </div>
  );
}
